import { useQuery } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import { LightbulbIcon } from 'lucide-react'
import { getSuggestedQestions } from '@/api/workstation'
import { Button } from '@/components/ui/button'
import { useTranslation } from 'react-i18next'
import { tokens } from '@/locales/translations/tokens'

interface SuggestedQuestionsProps {
  onQuestionClick: (question: string) => void
}

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
}

const item = {
  hidden: { opacity: 0, y: 20 },
  show: {
    opacity: 1,
    y: 0,
    transition: { type: 'spring', stiffness: 300, damping: 30 },
  },
}

export function SuggestedQuestions({
  onQuestionClick,
}: SuggestedQuestionsProps) {
  const { t } = useTranslation();
  const {
    data: suggestedQuestions = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['suggestedQuestions'],
    queryFn: getSuggestedQestions,
    staleTime: 15 * 1000,
  })

  if (isLoading) {
    return (
      <div className='w-full max-w-2xl space-y-3 pt-4'>
        <h3 className='text-sm font-medium text-foreground/70'>
          {t(tokens.workstation.loadingSuggestions)}
        </h3>
        <div className='flex flex-col space-y-2'>
          {[1, 2, 3, 4].map((index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0.5 }}
              animate={{ opacity: [0.5, 0.8, 0.5] }}
              transition={{
                repeat: Infinity,
                duration: 1.5,
                ease: 'easeInOut',
              }}
              className='h-10 rounded-xl bg-muted'
            />
          ))}
        </div>
      </div>
    )
  }

  if (isError || suggestedQuestions.length === 0) {
    return <></>
  }

  return (
    <div className='w-full max-w-2xl space-y-3 pt-0'>
      <h3 className='text-md font-medium text-foreground/70'>
        {t(tokens.workstation.start_with_suggested_question)}
      </h3>
      <AnimatePresence>
        <motion.div
          className='flex flex-col space-y-2 max-h-[300px] overflow-y-auto pr-2 max-sm:pb-5'
          variants={container}
          initial='hidden'
          animate='show'
        >
          {suggestedQuestions.map((question, index) => (
            <motion.div
              key={index}
              variants={item}
              exit={{ opacity: 0, y: -10 }}
            >
              <Button
                variant='outline'
                className='group h-auto justify-start whitespace-normal rounded-xl border-blue-200/50 bg-blue-50/50 px-4 py-2.5 text-left text-sm font-normal text-foreground/80 shadow-md transition-all hover:border-primary/50 hover:shadow-md dark:border-blue-800/30 dark:bg-blue-950/30 w-full'
                onClick={() => onQuestionClick(question)}
              >
                <LightbulbIcon className='mr-2 h-3.5 w-3.5 flex-shrink-0 text-amber-400/80' />
                <span>{question}</span>
              </Button>
            </motion.div>
          ))}
        </motion.div>
      </AnimatePresence>
    </div>
  )
}
